import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { PollService, isPollClosed, pollTimeRemaining } from '../../services/PollService';
import { useTheme } from '../ThemeProvider';
import type { Yap } from '../../types';

interface PollCardProps {
    yapId: string;
    poll: NonNullable<Yap['poll']>;
    /** Which collection the parent post lives in (yaps / forum_posts / tribe_posts). */
    collectionName?: string;
    /** Suppress voting, e.g. for a logged-out preview or a quoted yap. */
    readOnly?: boolean;
    /** Tight variant for small tap-to-detail tiles: shorter rows, no footer, and
     *  only the first couple of options with a "+N more" line. */
    compact?: boolean;
    /** Called before a vote is attempted; return false to block it (auth gate). */
    onBeforeVote?: () => boolean;
}

/**
 * A poll attached to a yap.
 *
 * Results stay hidden until the viewer votes or the poll closes, and that is a
 * DATA rule rather than a UI one: the tally lives in a separate pollResults
 * document whose read rule requires the caller's own vote to exist, so before
 * voting the numbers are not on the client to render. `counts` starts empty
 * here for exactly that reason.
 */
/**
 * Vote and tally, remembered for the session, keyed by post.
 *
 * Without this every mount restarted from "no vote, no results" and had to wait
 * two round-trips before it could draw anything — so scrolling a poll off screen
 * and back, or returning to the tab, showed an unfilled poll that then snapped
 * into place. The cache lets a remount paint the last known state immediately
 * while it revalidates behind that.
 */
const pollCache = new Map<string, { myVote: number | null; counts: Record<string, number>; total: number }>();

export function PollCard({ yapId, poll, collectionName = 'yaps', readOnly, compact, onBeforeVote }: PollCardProps) {
    const cacheKey = `${collectionName}:${yapId}`;
    const cached = pollCache.get(cacheKey);

    // Seeded synchronously from the cache so the first paint is already correct.
    const [myVote, setMyVote] = useState<number | null>(cached?.myVote ?? null);
    const [counts, setCounts] = useState<Record<string, number>>(cached?.counts ?? {});
    const [total, setTotal] = useState<number>(cached?.total ?? 0);
    const [haveResults, setHaveResults] = useState(!!cached);
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(!!cached);
    // Turnout for a viewer who has not voted. Null until fetched, and null for
    // polls whose votes all predate the public document — which renders as
    // nothing rather than as a misleading "0 votes".
    const [publicTotal, setPublicTotal] = useState<number | null>(null);

    const remember = (v: number | null, c: Record<string, number>, t: number) => {
        pollCache.set(cacheKey, { myVote: v, counts: c, total: t });
    };
    const { theme } = useTheme();
    const dark = theme === 'dark';

    // src/index.css is committed, pre-compiled Tailwind, so a utility that is not
    // already in it silently renders nothing. Several of the shades this card
    // needs (bg-black/[0.02], bg-[#4478ea]/25, bg-black/[0.06]) are absent, which
    // left the result bars with no fill at all. Anything colour-critical is set
    // inline so it cannot depend on what happens to be in that stylesheet.
    const surfaceBg = dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)';
    // Options fill left to right in proportion to their share.
    //
    // The option in FRONT fills white, so the winner is unmistakable at a
    // glance. Your own pick is marked separately, by the check and the accent
    // border, so the two readings stay independent: white answers "which is
    // winning", the check answers "which did I choose". Every other option
    // keeps the subtle tint, and an option with no votes gets no bar at all —
    // a zero-width fill on an untouched row is just noise.
    const leadBar = '#FFFFFF';
    const otherBar = dark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)';

    const closed = isPollClosed(poll);

    const loadResults = async () => {
        const r = await PollService.fetchResults(yapId, collectionName, poll);
        if (r) {
            setCounts(r.counts);
            setTotal(r.totalVotes);
            setHaveResults(true);
            remember(myVoteRef.current, r.counts, r.totalVotes);
        }
        return r;
    };

    // The loader reads the latest vote without being re-created for it, so a
    // vote cast mid-flight still lands in the cache under the right value.
    const myVoteRef = useRef<number | null>(myVote);
    useEffect(() => { myVoteRef.current = myVote; }, [myVote]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Both reads are entitled once a vote exists, so they go together
            // rather than one after the other — on a cold mount that halved the
            // wait before anything could be drawn.
            const [v, cachedResults] = await Promise.all([
                PollService.getMyVote(yapId, collectionName),
                closed ? PollService.fetchResults(yapId, collectionName, poll) : Promise.resolve(null),
            ]);
            if (cancelled) return;
            setMyVote(v);
            setLoaded(true);

            let results = cachedResults;
            // Only fetch the tally when entitled to it. Asking otherwise just
            // earns a permission-denied, which is the rule doing its job.
            if (!results && v !== null) {
                results = await PollService.fetchResults(yapId, collectionName, poll);
            }
            if (cancelled) return;

            if (results) {
                setCounts(results.counts);
                setTotal(results.totalVotes);
                setHaveResults(true);
                remember(v, results.counts, results.totalVotes);
            } else if (v === null && !closed) {
                // Genuinely no entitlement yet — drop any stale cached tally so a
                // fresh viewer is not shown numbers they have not earned.
                setHaveResults(false);
                pollCache.delete(cacheKey);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yapId, collectionName, closed]);

    // Results appear once the viewer has voted, or once voting has ended — and
    // only once the numbers have actually arrived.
    const showResults = (closed || myVote !== null) && haveResults;
    // A first vote cannot show percentages instantly: the tally was unreadable
    // until the vote existed, so there is genuinely nothing to draw until it
    // returns. Marking the row as settling keeps that moment from reading as
    // "the tap did nothing" without inventing numbers to fill it.
    const settling = myVote !== null && !haveResults && !closed;
    // Single gate for the trough, the fill and the dark label, so they can never
    // disagree — a grey row with no bar, or dark text on a dark row, would each
    // be a legibility bug on its own.
    const showingBars = showResults && loaded;

    useEffect(() => {
        // Only while the tally is out of reach. Once it is readable it carries
        // the same number and is authoritative, so this would be a second read
        // of a fact already in hand.
        if (showResults) return;
        // Subscribed, not fetched once. A poll gaining votes while someone reads
        // it used to keep showing the number it had when the card mounted, so a
        // poll on eight votes still read "5 votes" until the screen was left and
        // come back to. Only the total streams — the split stays behind the
        // tally's read rule, so this shows that a poll is busy without showing
        // which way it is going.
        return PollService.subscribeToPublicTotal(yapId, collectionName, setPublicTotal);
    }, [yapId, collectionName, showResults]);

    // A vote is final, so once one exists every option is inert. The rules deny
    // the change too; this is what stops the reader trying.
    const canVote = !readOnly && !closed && myVote === null;

    const vote = async (index: number) => {
        if (!canVote || busy) return;
        if (onBeforeVote && !onBeforeVote()) return;
        setBusy(true);

        const previous = myVote;
        setMyVote(index);

        // Only adjust locally when the numbers are already on screen. On a first
        // vote they are not — the tally was unreadable until this moment — so
        // there is nothing to move, and it is fetched below instead.
        if (haveResults) {
            setCounts(prev => {
                const next = { ...prev };
                next[String(index)] = (next[String(index)] || 0) + 1;
                if (typeof previous === 'number') next[String(previous)] = Math.max(0, (next[String(previous)] || 0) - 1);
                return next;
            });
            if (previous === null) setTotal(t => t + 1);
        }

        const applied = await PollService.castVote(yapId, index, collectionName);
        if (applied === null) {
            // Roll the optimistic move back rather than leaving a vote on screen
            // that was never written — and drop the cache with it, so a remount
            // does not resurrect the vote that failed.
            setMyVote(previous);
            if (previous === null) {
                setHaveResults(false);
                setCounts({});
                setTotal(0);
                pollCache.delete(cacheKey);
            } else {
                await loadResults();
            }
        } else {
            // The vote now exists, so the read rule admits us. Always refresh:
            // the local guess covers only this vote, while the tally includes
            // everyone else's since the card was last loaded.
            await loadResults();
        }
        setBusy(false);
    };

    const pct = (i: number) => {
        if (!total) return 0;
        return Math.round(((counts[String(i)] || 0) / total) * 100);
    };

    // What the footer shows. The tally is authoritative once it is readable;
    // before that the public total stands in. Preferring the tally keeps the
    // number from stepping backwards at the moment of voting, since the public
    // doc is only accurate from its own first write onward.
    const turnout = showResults ? total : publicTotal;

    // White marks the option in front, not the viewer's own vote — which is
    // marked by the check and the accent border instead.
    //
    // Compared on raw counts rather than the rounded percentages: two options
    // on 33.4% both display as 33 without being tied, and only the counts say
    // which is actually ahead. Genuine ties are all highlighted, since they are
    // all equally in front. Guarded on a non-zero leader so a poll where no
    // option has taken a vote does not crown the first one.
    const topCount = Math.max(0, ...poll.options.map((_, i) => counts[String(i)] || 0));
    const isLeading = (i: number) => showingBars && topCount > 0 && (counts[String(i)] || 0) === topCount;

    // Compact tiles show only the first couple of options; the rest are summarised.
    const visibleOptions = compact ? poll.options.slice(0, 2) : poll.options;
    const hiddenCount = poll.options.length - visibleOptions.length;

    return (
        <div
            className={`rounded-xl border border-black/10 dark:border-white/10 ${compact ? 'mt-2 p-2' : 'mt-3 p-3'}`}
            style={{ backgroundColor: surfaceBg }}
            // Swallowing the click keeps a tap on an option from also opening the
            // yap. A read-only card has nothing to swallow it FOR, and doing so
            // would turn the poll into a dead patch on a tap-to-open tile.
            onClick={readOnly ? undefined : (e) => e.stopPropagation()}
        >
            <div className={compact ? 'space-y-1' : 'space-y-3'}>
                {visibleOptions.map((option, i) => {
                    const chosen = myVote === i;
                    const leading = isLeading(i);
                    const percent = pct(i);
                    return (
                        <button
                            key={i}
                            type="button"
                            disabled={!canVote || busy}
                            onClick={() => vote(i)}
                            className={`relative w-full overflow-hidden rounded-lg border text-left transition-colors ${
                                chosen
                                    ? 'border-[#4478ea]'
                                    : 'border-black/10 dark:border-white/10'
                            } ${!canVote ? 'cursor-default' : 'cursor-pointer active:scale-[0.99]'}`}
                            // Everything except the row just chosen dims while the
                            // tally is on its way, so the choice reads as taken.
                            style={settling && !chosen ? { opacity: 0.45 } : undefined}
                        >
                            {/* Fills left to right, width = this option's share.
                                Only once results are unlocked — a partly filled bar
                                beforehand would leak the split this is built to hide.
                                Options with no votes are left alone entirely. */}
                            {showingBars && (chosen || percent > 0) && (
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${percent}%` }}
                                    transition={{ duration: 0.35, ease: 'easeOut' }}
                                    // top-0 bottom-0 rather than inset-y-0: index.css
                                    // is committed pre-compiled Tailwind and has no
                                    // inset-x-*/inset-y-* utilities at all, so
                                    // inset-y-0 gave the bar no top or bottom and it
                                    // resolved to zero height — correctly sized and
                                    // coloured, painting nothing.
                                    className="absolute top-0 bottom-0 left-0"
                                    style={{ backgroundColor: leading ? leadBar : otherBar }}
                                />
                            )}
                            {/* py-4 rather than a tighter arbitrary value: the
                                compiled stylesheet has py-4 but not py-3.5, and a
                                class it does not contain renders nothing. */}
                            {/* items-start, not items-center: a wrapped label should
                                grow the row downward with the check and the
                                percentage staying by the first line, rather than
                                floating to the middle of a three-line option. */}
                            <div className={`relative flex items-start justify-between gap-2 px-3 ${compact ? 'py-2' : 'py-4'}`}>
                                <span
                                    className="flex items-start gap-2 min-w-0 text-sm"
                                    // Blue on the leading row. It reads against the
                                    // white fill AND against the unfilled part of the
                                    // row, which is what the previous dark treatment
                                    // could not do: dark only worked where the fill
                                    // actually reached, so it needed a second clipped
                                    // copy of the whole row to stay legible. Blue
                                    // needs none of that.
                                    style={{ color: leading ? '#4478ea' : (dark ? '#FFFFFF' : '#111111') }}
                                >
                                    {chosen && <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#4478ea]" />}
                                    {/* Wraps instead of truncating: the whole option
                                        is the thing being voted on, so cutting it off
                                        mid-word hides what the vote is actually for.
                                        break-words so a single long token still
                                        breaks rather than overflowing the row. */}
                                    <span className="break-words">{option}</span>
                                </span>
                                {showingBars && (
                                    <span
                                        className="shrink-0 text-xs font-semibold"
                                        style={{ color: leading ? '#4478ea' : (dark ? '#FFFFFF' : '#111111') }}
                                    >
                                        {percent}%
                                    </span>
                                )}
                            </div>

                        </button>
                    );
                })}
            </div>

            {/* Compact tiles say how many options were not shown, instead of the
                full footer — the tile is a preview, and tapping opens the real one. */}
            {compact && hiddenCount > 0 && (
                <p className="mt-1 px-1 text-xs" style={{ color: dark ? '#a0a0a0' : '#555555' }}>
                    +{hiddenCount} more {hiddenCount === 1 ? 'option' : 'options'}
                </p>
            )}

            <div
                className={`flex items-center gap-2 text-xs ${compact ? 'mt-1 px-1' : 'mt-2'}`}
                style={{ color: dark ? '#a0a0a0' : '#555555' }}
            >
                {/* Turnout shows either way. Once results are unlocked it comes
                    from the tally; before that from the public total, which is a
                    separate document precisely so how MANY have voted can be
                    shown without revealing how they split. */}
                {turnout !== null && (
                    <>
                        <span>{turnout} {turnout === 1 ? 'vote' : 'votes'}</span>
                        <span>·</span>
                    </>
                )}
                <span>{pollTimeRemaining(poll)}</span>
                {!showResults && !closed && <><span>·</span><span>Vote to see results</span></>}
            </div>
        </div>
    );
}
